/**
 * Before/After Gallery Interactive Component
 * Provides drag, touch, and keyboard functionality for before/after image comparison
 */

class BeforeAfterGallery {
    constructor(element) {
        this.container = element;
        this.scrubber = this.container.querySelector('.before-after-gallery__scrubber-handle');
        this.afterImage = this.container.querySelector('.before-after-gallery__image--after');
        this.isDragging = false;
        this.startX = 0;
        this.currentX = 0;
        
        // Get initial position from data attribute
        this.position = parseInt(this.container.dataset.scrubberPosition) || 50;
        
        this.init();
    }
    
    init() {
        if (!this.scrubber || !this.afterImage) return;
        
        // Set initial position
        this.updatePosition(this.position);
        
        // Bind events
        this.bindEvents();
        
        // Add loaded class for CSS animations
        this.container.classList.add('before-after-gallery--loaded');
    }
    
    bindEvents() {
        // Mouse events
        this.scrubber.addEventListener('mousedown', this.handleStart.bind(this));
        document.addEventListener('mousemove', this.handleMove.bind(this));
        document.addEventListener('mouseup', this.handleEnd.bind(this));
        
        // Touch events
        this.scrubber.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
        document.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
        document.addEventListener('touchend', this.handleEnd.bind(this));
        
        // Keyboard events
        this.scrubber.addEventListener('keydown', this.handleKeydown.bind(this));
        
        // Prevent context menu on long press
        this.scrubber.addEventListener('contextmenu', (e) => e.preventDefault());
    }
    
    handleStart(e) {
        this.isDragging = true;
        this.container.classList.add('before-after-gallery--dragging');
        
        // Get starting position
        const rect = this.container.getBoundingClientRect();
        this.startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
        this.currentX = this.startX;
        
        // Prevent default to avoid text selection
        e.preventDefault();
    }
    
    handleMove(e) {
        if (!this.isDragging) return;
        
        e.preventDefault();
        
        // Get current position
        const rect = this.container.getBoundingClientRect();
        this.currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
        
        // Calculate percentage
        const deltaX = this.currentX - rect.left;
        const percentage = Math.max(0, Math.min(100, (deltaX / rect.width) * 100));
        
        this.updatePosition(percentage);
    }
    
    handleEnd() {
        if (!this.isDragging) return;
        
        this.isDragging = false;
        this.container.classList.remove('before-after-gallery--dragging');
    }
    
    handleKeydown(e) {
        let newPosition = this.position;
        
        switch(e.key) {
            case 'ArrowLeft':
                newPosition = Math.max(0, this.position - 5);
                e.preventDefault();
                break;
            case 'ArrowRight':
                newPosition = Math.min(100, this.position + 5);
                e.preventDefault();
                break;
            case 'Home':
                newPosition = 0;
                e.preventDefault();
                break;
            case 'End':
                newPosition = 100;
                e.preventDefault();
                break;
            default:
                return;
        }
        
        this.updatePosition(newPosition);
    }
    
    updatePosition(percentage) {
        this.position = percentage;
        
        // Update scrubber position
        this.scrubber.style.left = `${percentage}%`;
        this.scrubber.setAttribute('aria-valuenow', Math.round(percentage));
        
        // Update after image clip (clips from the left to reveal more as you drag right)
        this.afterImage.style.clipPath = `inset(0 0 0 ${percentage}%)`;
        
        // Update labels visibility
        const beforeLabel = this.container.querySelector('.before-after-gallery__label--before');
        const afterLabel = this.container.querySelector('.before-after-gallery__label--after');
        
        if (beforeLabel && afterLabel) {
            if (percentage < 20) {
                // Less clipping = more "After" image visible
                beforeLabel.style.opacity = '0.3';
                afterLabel.style.opacity = '1';
            } else if (percentage > 80) {
                // More clipping = more "Before" image visible
                beforeLabel.style.opacity = '1';
                afterLabel.style.opacity = '0.3';
            } else {
                // Balanced view
                beforeLabel.style.opacity = '0.7';
                afterLabel.style.opacity = '0.7';
            }
        }
    }
}

// Initialize all before/after galleries when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    const galleries = document.querySelectorAll('.before-after-gallery');
    galleries.forEach(gallery => {
        new BeforeAfterGallery(gallery);
    });
});

// Re-initialize galleries when new content is loaded (for AJAX)
document.addEventListener('before-after-gallery-init', function() {
    const galleries = document.querySelectorAll('.before-after-gallery:not(.before-after-gallery--loaded)');
    galleries.forEach(gallery => {
        new BeforeAfterGallery(gallery);
    });
});
